// Cohort / attribution fingerprint signals, collected once per page load and nested under
// `bd.cf`, plus the shared plumbing the nine legacy `bd` reads use.
//
// This file is a hand-copy of the snapps collector at
// `src/packages/api/telemetry/src/analytics/cohortProps.ts`. The emitted JSON must be
// identical on the same machine so the two products' data can be compared directly.
//
// TODO: the duplication is temporary and should be replaced by a minimal zero-dependency
// internal package consumed by both repos. Until then the two copies are kept in step BY
// HAND — there is no automated cross-repo check, so mirror every change in the same review.

// ---------------------------------------------------------------------------
// What we emit
// ---------------------------------------------------------------------------

/** Numeric WebGL limits, as reported by `getParameter`. */
export interface GlLimits {
  mts?: number | null;
  mvuv?: number | null;
  mfuv?: number | null;
  mvv?: number | null;
  mva?: number | null;
  mctiu?: number | null;
  mtiu?: number | null;
  /**
   * A `Float32Array`, so this serialises as an OBJECT with numeric string keys
   * (`{"0":1,"1":511}`), not a JSON array. Query it as `apsr:"0"`, not `apsr[0]`.
   */
  apsr?: Float32Array | null;
  /** `null` when EXT_texture_filter_anisotropic is unavailable. */
  aniso?: number | null;
}

export interface GlProps {
  /** Unmasked vendor. `null` when the browser withholds it. */
  uv?: string | null;
  /** Unmasked renderer, capped at 128 characters. `null` when withheld. */
  ur?: string | null;
  ver?: string | null;
  /** Masked vendor. Present only as a fallback, when `ur` was unreadable. */
  v?: string | null;
  /** Masked renderer. Present only as a fallback, when `ur` was unreadable. */
  r?: string | null;
  /** Supported extension names, sorted and capped at 64 entries. */
  ext?: string[] | null;
  lim?: GlLimits | null;
  /** `[rangeMin, rangeMax, precision]` for the vertex then fragment shader. */
  prec?: ([number, number, number] | null)[] | null;
  attrs?: Record<string, unknown> | null;
}

export interface LocaleProps {
  tz?: string | null;
  lc?: string | null;
  tzo?: number | null;
  /** Full ordered list: the ordering carries more entropy than `languages[0]` alone. */
  langs?: string[] | null;
}

export interface DisplayProps {
  dpr?: number | null;
  cd?: number | null;
  aw?: number | null;
  ah?: number | null;
  or?: string | null;
  ora?: number | null;
}

export interface InputProps {
  mtp?: number | null;
  ptr?: 'none' | 'coarse' | 'fine' | null;
  hov?: boolean | null;
}

export interface PrefProps {
  cs?: 'dark' | 'light' | null;
  rm?: boolean | null;
  rt?: boolean | null;
  fc?: boolean | null;
  ct?: 'more' | 'less' | 'no-preference' | null;
  gam?: 'rec2020' | 'p3' | 'srgb' | null;
}

export interface DeviceProps {
  dm?: number | null;
  hl?: number | null;
  pdf?: boolean | null;
}

export interface MediaProps {
  /** Values are `"probably"`, `"maybe"` or `""`. */
  cpt?: Record<string, string> | null;
  mr?: string[] | null;
}

export interface UaChProps {
  /** Includes a per-request GREASE entry: ignore it server-side. Capped at 16 entries. */
  brands?: { brand?: string; version?: string }[] | null;
  mobile?: boolean | null;
  platform?: string | null;
}

export interface CohortProps {
  /** Schema version. Not optional: these fields will churn as browsers reduce surfaces. */
  sv: number;
  /** `null` when no WebGL context could be created. */
  gl?: GlProps | null;
  /**
   * WebGL2 support. A property of the window, not of a context, so it lives here rather than
   * inside `gl` — a browser that blocks the WebGL1 context still reports it.
   */
  g2?: boolean | null;
  loc?: LocaleProps;
  dsp?: DisplayProps;
  inp?: InputProps;
  pref?: PrefProps;
  dev?: DeviceProps;
  med?: MediaProps;
  /** `null` when the browser does not implement UA Client Hints. */
  uad?: UaChProps | null;
  /** Names of fields whose read threw. Absent when nothing threw. */
  err?: string[];
}

/**
 * The nine legacy bot-detection signals plus the cohort bundle. Declared here rather than in
 * the hosts so both repos return the same type and it stays next to {@link CohortProps}.
 */
export interface BotDetectionProps {
  wd: boolean | null;
  pl: number;
  mtl: number;
  sw: number;
  sh: number;
  pf: string;
  hwc: number;
  el: number;
  /** `null` when the probe itself threw, which is an automation artifact in its own right. */
  sl: boolean | null;
  cf: CohortProps;
}

const SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------
// Non-standard globals, absent from the standard lib types
// ---------------------------------------------------------------------------

interface NonStandardNavigator {
  /** Chromium only. */
  deviceMemory?: number;
  pdfViewerEnabled?: boolean;
  /** Sync surface only; `getHighEntropyValues()` is async and out of scope. */
  userAgentData?: {
    brands?: { brand: string; version: string }[];
    mobile?: boolean;
    platform?: string;
  };
}

/** Chromium-only `performance.memory`. */
interface NonStandardPerformance {
  memory?: { jsHeapSizeLimit?: number };
}

/** `navigator` and `performance` widened to include the non-standard fields above. */
type NavX = Navigator & NonStandardNavigator;
type PerfX = Performance & NonStandardPerformance;

// ---------------------------------------------------------------------------
// Reading safely
// ---------------------------------------------------------------------------

/** Returns `undefined` only when `read` throws; `null` is a real value. */
export type Safe = <T>(name: string, read: () => T) => T | undefined;

export interface Reader {
  safe: Safe;
  /**
   * Reads through {@link Safe}, collapsing both "API absent" and "the read threw" into one
   * sentinel. Every legacy `bd` field wants those two outcomes to agree, so the sentinel is
   * named once instead of appearing on both sides of a `??`.
   */
  read: <T>(name: string, sentinel: T, get: () => T | null | undefined) => T;
  err: string[];
}

/**
 * Collects field-level failures without aborting the bundle.
 *
 * Three outcomes stay distinguishable, because they mean different things to an analyst: a
 * value was read; the API is absent or the browser withheld it (`null`, retained); or the
 * read threw (key omitted, name appended to `err`). Per-field rather than one `try/catch`
 * around the whole bundle, so a single failed WebGL read cannot discard the other forty.
 *
 * Exported so the legacy `bd` reads share the same mechanism and report into the same `err`
 * list, under `bd.`-prefixed names.
 */
export function makeReader(): Reader {
  const err: string[] = [];
  const safe: Safe = (name, read) => {
    try {
      return read();
    } catch {
      err.push(name);
      return undefined;
    }
  };
  const read: Reader['read'] = (name, sentinel, get) => safe(name, get) ?? sentinel;
  return { safe, read, err };
}

/**
 * Merges extra failure names into `cf.err`. Returns the argument untouched when there are
 * none, so the common path allocates nothing and the collector's cache identity survives.
 */
export function withErrors(cf: CohortProps, extra: string[]): CohortProps {
  if (extra.length === 0) return cf;
  // `bd` reads happen before the cohort groups are collected, so they come first.
  return { ...cf, err: [...extra, ...(cf.err ?? [])] };
}

// ---------------------------------------------------------------------------
// Bounding host-supplied values
// ---------------------------------------------------------------------------

// A page that redefines `getParameter`, `languages` or `userAgentData` can return arbitrarily
// large values, and this payload rides every telemetry event, so each variable-length field
// is bounded here rather than trusted. Non-string / non-array inputs pass through untouched,
// so `null` stays `null`.

/** Caps a string; anything else passes through unchanged. */
function capStr<T>(value: T, max = 128): T {
  return typeof value === 'string' ? (value.slice(0, max) as T) : value;
}

/** Caps an array's length and each of its string entries. */
function capList<T>(value: T, maxItems: number, maxLen = 64): T {
  return Array.isArray(value) ? (value.slice(0, maxItems).map(v => capStr(v, maxLen)) as T) : value;
}

/**
 * The result of {@link compact}: every key optional, and none of them explicitly
 * `undefined`. Spelling this out rather than using `Partial<T>` is what lets the collectors
 * return their real types under `exactOptionalPropertyTypes`.
 */
type Compacted<T> = { [K in keyof T]?: Exclude<T[K], undefined> };

/** Drops `undefined` keys; preserves `null`. */
function compact<T extends object>(obj: T): Compacted<T> {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as Compacted<T>;
}

/** `getContextAttributes()` is host-provided, so project only the keys we expect. */
const CONTEXT_ATTRS = [
  'alpha',
  'antialias',
  'depth',
  'desynchronized',
  'failIfMajorPerformanceCaveat',
  'powerPreference',
  'premultipliedAlpha',
  'preserveDrawingBuffer',
  'stencil',
  'xrCompatible',
] as const;

function pickContextAttrs(value: unknown): Record<string, unknown> | null | undefined {
  if (value === null || value === undefined) return value;
  if (typeof value !== 'object') return null;
  const src = value as Record<string, unknown>;
  return compact(Object.fromEntries(CONTEXT_ATTRS.map(k => [k, capStr(src[k], 32)])));
}

/** Matches a media query. Used by the input and preference collectors. */
const mq = (q: string): boolean => window.matchMedia(q).matches;

// ---------------------------------------------------------------------------
// Collectors, one per group
// ---------------------------------------------------------------------------

/**
 * One canvas, one context, all reads, then explicit release. Browsers cap concurrent WebGL
 * contexts (commonly ~16); leaking them will break the page's own rendering.
 *
 * Returns `null` when no context could be created, which is distinct from a read that threw.
 */
function collectWebGl(safe: Safe): GlProps | null {
  // The canvas is created INSIDE `safe`: anti-fingerprinting extensions patch
  // `document.createElement` and `getContext` to throw, and this function must not.
  const gl =
    safe('gl.ctx', () => {
      const canvas = document.createElement('canvas');
      return (canvas.getContext('webgl') ??
        canvas.getContext('experimental-webgl')) as WebGLRenderingContext | null;
    }) ?? null;

  // No context is distinct from "context threw", which lands in `err` instead.
  if (!gl) return null;

  const dbg = safe('gl.dbg', () => gl.getExtension('WEBGL_debug_renderer_info'));
  const aniso = safe('gl.aniso', () => gl.getExtension('EXT_texture_filter_anisotropic'));

  // Unmasked identity. `null` when the browser withholds it — recorded, not dropped.
  const uv = dbg ? safe('gl.uv', () => gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL)) : null;
  const ur = dbg ? safe('gl.ur', () => gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : null;

  const out: GlProps = compact({
    uv: capStr(uv),
    ur: capStr(ur),
    ver: capStr(safe('gl.ver', () => gl.getParameter(gl.VERSION))),

    // The masked pair is near-constant per engine, so it is a fallback only.
    v: ur ? undefined : capStr(safe('gl.v', () => gl.getParameter(gl.VENDOR))),
    r: ur ? undefined : capStr(safe('gl.r', () => gl.getParameter(gl.RENDERER))),

    // Real browsers report ~39; 64 leaves headroom without trusting the host.
    ext: capList(
      safe('gl.ext', () => (gl.getSupportedExtensions() ?? []).slice().sort()),
      64,
    ),

    lim: safe('gl.lim', () => ({
      mts: gl.getParameter(gl.MAX_TEXTURE_SIZE),
      mvuv: gl.getParameter(gl.MAX_VERTEX_UNIFORM_VECTORS),
      mfuv: gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS),
      mvv: gl.getParameter(gl.MAX_VARYING_VECTORS),
      mva: gl.getParameter(gl.MAX_VERTEX_ATTRIBS),
      mctiu: gl.getParameter(gl.MAX_COMBINED_TEXTURE_IMAGE_UNITS),
      mtiu: gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS),
      apsr: gl.getParameter(gl.ALIASED_POINT_SIZE_RANGE),
      aniso: aniso ? gl.getParameter(aniso.MAX_TEXTURE_MAX_ANISOTROPY_EXT) : null,
    })),

    // HIGH_FLOAT is the entry that discriminates; LOW/MEDIUM and the INT variants are
    // near-universally identical to it.
    prec: safe('gl.prec', () =>
      (['VERTEX_SHADER', 'FRAGMENT_SHADER'] as const).map(stage => {
        const f = gl.getShaderPrecisionFormat(gl[stage], gl.HIGH_FLOAT);
        return f ? ([f.rangeMin, f.rangeMax, f.precision] as [number, number, number]) : null;
      }),
    ),

    attrs: pickContextAttrs(safe('gl.attrs', () => gl.getContextAttributes())),
  });

  // Release GPU resources; do not rely on GC. The extension is optional, so when it is
  // missing the context is left to GC and nothing more can be done from here.
  safe('gl.lose', () => gl.getExtension('WEBGL_lose_context')?.loseContext());
  return out;
}

function collectLocale(safe: Safe): LocaleProps {
  const ro = safe('loc.ro', () => Intl.DateTimeFormat().resolvedOptions());
  return compact({
    // Read through `safe` as well: `ro` is host-provided, so its property getters can throw.
    tz: capStr(
      safe('loc.tz', () => ro?.timeZone),
      64,
    ),
    lc: capStr(
      safe('loc.lc', () => ro?.locale),
      64,
    ),
    tzo: safe('loc.tzo', () => new Date().getTimezoneOffset()),
    langs: capList(
      safe('loc.langs', () => navigator.languages?.slice()),
      16,
      32,
    ),
  });
}

function collectDisplay(safe: Safe): DisplayProps {
  return compact({
    dpr: safe('dsp.dpr', () => window.devicePixelRatio),
    cd: safe('dsp.cd', () => screen.colorDepth),
    aw: safe('dsp.aw', () => screen.availWidth),
    ah: safe('dsp.ah', () => screen.availHeight),
    or: capStr(
      safe('dsp.or', () => screen.orientation?.type ?? null),
      32,
    ),
    ora: safe('dsp.ora', () => screen.orientation?.angle ?? null),
  });
}

function collectInput(safe: Safe): InputProps {
  return compact({
    mtp: safe('inp.mtp', () => navigator.maxTouchPoints),
    ptr: safe(
      'inp.ptr',
      () => (['none', 'coarse', 'fine'] as const).find(v => mq(`(pointer: ${v})`)) ?? null,
    ),
    hov: safe('inp.hov', () => mq('(hover: hover)')),
  });
}

function collectPrefs(safe: Safe): PrefProps {
  return compact({
    cs: safe('pref.cs', (): 'dark' | 'light' =>
      mq('(prefers-color-scheme: dark)') ? 'dark' : 'light',
    ),
    rm: safe('pref.rm', () => mq('(prefers-reduced-motion: reduce)')),
    rt: safe('pref.rt', () => mq('(prefers-reduced-transparency: reduce)')),
    fc: safe('pref.fc', () => mq('(forced-colors: active)')),
    ct: safe('pref.ct', (): 'more' | 'less' | 'no-preference' =>
      mq('(prefers-contrast: more)')
        ? 'more'
        : mq('(prefers-contrast: less)')
          ? 'less'
          : 'no-preference',
    ),
    gam: safe(
      'pref.gam',
      () => (['rec2020', 'p3', 'srgb'] as const).find(v => mq(`(color-gamut: ${v})`)) ?? null,
    ),
  });
}

function collectDevice(safe: Safe): DeviceProps {
  // Each property is read inside `safe`, so a hostile getter degrades one field.
  return compact({
    dm: safe('dev.dm', () => (navigator as NavX).deviceMemory ?? null),
    hl: safe('dev.hl', () => (performance as PerfX).memory?.jsHeapSizeLimit ?? null),
    pdf: safe('dev.pdf', () => {
      const enabled = (navigator as NavX).pdfViewerEnabled;
      return typeof enabled === 'boolean' ? enabled : null;
    }),
  });
}

const VIDEO_CODECS = {
  h264: 'video/mp4; codecs="avc1.42E01E"',
  hevc: 'video/mp4; codecs="hvc1"',
  av1: 'video/mp4; codecs="av01.0.00M.08"',
  vp9: 'video/webm; codecs="vp9"',
} as const;

const AUDIO_CODECS = {
  ogg: 'audio/ogg; codecs="vorbis"',
  aac: 'audio/mp4; codecs="mp4a.40.2"',
  flac: 'audio/flac',
} as const;

const RECORDER_TYPES = [
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/mp4;codecs=avc1',
] as const;

function collectMedia(safe: Safe): MediaProps {
  return compact({
    cpt: safe('med.cpt', () => {
      const v = document.createElement('video');
      const a = document.createElement('audio');
      return {
        ...Object.fromEntries(Object.entries(VIDEO_CODECS).map(([k, t]) => [k, v.canPlayType(t)])),
        ...Object.fromEntries(Object.entries(AUDIO_CODECS).map(([k, t]) => [k, a.canPlayType(t)])),
      };
    }),
    mr: safe('med.mr', () =>
      typeof MediaRecorder !== 'undefined'
        ? RECORDER_TYPES.filter(t => MediaRecorder.isTypeSupported(t))
        : null,
    ),
  });
}

function collectUaCh(safe: Safe): UaChProps | null {
  const uad = safe('uad', () => (navigator as NavX).userAgentData);
  if (!uad) return null;
  return compact({
    brands: safe('uad.brands', () =>
      uad.brands?.slice(0, 16).map(b => ({
        brand: capStr(b?.brand, 64),
        version: capStr(b?.version, 32),
      })),
    ),
    mobile: safe('uad.mobile', () => uad.mobile),
    platform: capStr(
      safe('uad.platform', () => uad.platform),
      64,
    ),
  });
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

function collect(): CohortProps {
  try {
    const { safe, err } = makeReader();

    return {
      sv: SCHEMA_VERSION,
      ...compact({
        gl: collectWebGl(safe),
        // Constructor check, not a second context: creating one measured 1.57 ms, about 40%
        // of the whole bundle, for a boolean this produces in 0.005 ms with the same result.
        g2: safe('g2', () => typeof WebGL2RenderingContext !== 'undefined') ?? null,
        loc: collectLocale(safe),
        dsp: collectDisplay(safe),
        inp: collectInput(safe),
        pref: collectPrefs(safe),
        dev: collectDevice(safe),
        med: collectMedia(safe),
        uad: collectUaCh(safe),
        err: err.length ? err : undefined,
      }),
    };
  } catch {
    // Backstop only. Every read above is individually isolated, so reaching this means a host
    // global itself is hostile.
    return { sv: SCHEMA_VERSION, err: ['cf.fatal'] };
  }
}

/**
 * Builds a collector that computes the bundle once and caches it.
 *
 * The cache is required, not an optimisation: every host reads props per telemetry commit,
 * and WebGL context creation can trigger GPU-process startup. Caching the failure case too
 * matters — retrying would re-enter the WebGL path on every event.
 *
 * Exported so tests get an isolated instance instead of having to reset shared state.
 */
export function createCohortCollector(): () => CohortProps {
  let cached: CohortProps | undefined;
  return () => (cached ??= collect());
}

/** The page-load singleton every host shares. */
export const getCohortProps = createCohortCollector();
